/**
 * Erreurs de l'API, au format RFC 7807.
 *
 * Deux familles, et il faut savoir les distinguer parce qu'elles n'appellent
 * pas la même réaction :
 *
 * - `ProblemError` — le serveur a répondu et il refuse. Le refus est décrit par
 *   un `code` machine stable ; c'est **lui** qu'on teste, jamais `detail`, qui
 *   est du texte traduisible.
 * - `NetworkError` — le serveur n'a pas répondu : coupure, DNS, requête
 *   annulée, réponse illisible. Rien à dire à l'utilisateur sur le fond, tout à
 *   dire sur le fait qu'il peut réessayer.
 */
import type { Bucket, Int64, Problem } from "./types";

/**
 * Jetons machine émis par le back. La liste est celle de
 * `internal/adapter/in/http/errors.go`, tenue à jour avec le contrat.
 *
 * Le type reste ouvert (`| (string & {})`) : un code inconnu d'un front plus
 * ancien que le serveur doit rester manipulable, pas provoquer une erreur de
 * compilation chez celui qui l'attrape.
 */
export const PROBLEM_CODES = {
  // 400 — la requête n'est pas lisible
  malformedBody: "malformed_body",
  unknownField: "unknown_field",
  invalidPathParameter: "invalid_path_parameter",
  invalidQueryParameter: "invalid_query_parameter",
  invalidHeader: "invalid_header",
  idempotencyKeyRequired: "idempotency_key_required",
  // 404 — la ressource n'existe pas
  walletNotFound: "wallet_not_found",
  packNotFound: "pack_not_found",
  expertiseNotFound: "expertise_not_found",
  gridNotFound: "grid_not_found",
  gridConflict: "grid_conflict",
  expertiseRetired: "expertise_retired",
  missionNotFound: "mission_not_found",
  entryNotFound: "entry_not_found",
  routeNotFound: "route_not_found",
  // 405 / 408
  methodNotAllowed: "method_not_allowed",
  clientClosedRequest: "client_closed_request",
  // 409 — l'état courant refuse
  insufficientFunds: "insufficient_funds",
  envelopeOverrun: "envelope_overrun",
  invalidTransition: "invalid_transition",
  // 413 / 415
  bodyTooLarge: "body_too_large",
  unsupportedMediaType: "unsupported_media_type",
  // 422 — le domaine refuse sur le fond
  invalidField: "invalid_field",
  invalidAmount: "invalid_amount",
  invalidLabel: "invalid_label",
  invalidEnvelope: "invalid_envelope",
  invalidId: "invalid_id",
  walletRequired: "wallet_required",
  incompleteReference: "incomplete_reference",
  // 500 / 503 / 504
  internalError: "internal_error",
  dependencyUnavailable: "dependency_unavailable",
  timeout: "timeout",

  // 401 / 403 — session et droits
  unauthenticated: "unauthenticated",
  invalidCredentials: "invalid_credentials",
  forbidden: "forbidden",
  roleNotAssignable: "role_not_assignable",
  csrfRejected: "csrf_rejected",
  // 404 — comptes
  companyNotFound: "company_not_found",
  userNotFound: "user_not_found",
  invitationNotFound: "invitation_not_found",
  logoNotFound: "logo_not_found",
  signupNotFound: "signup_not_found",
  // 409 — comptes
  invitationUsed: "invitation_used",
  emailTaken: "email_taken",
  lastAdmin: "last_admin",
  userNotInvited: "user_not_invited",
  selfTarget: "self_target",
  signupAlreadyDecided: "signup_already_decided",
  // 410
  invitationExpired: "invitation_expired",
  passwordResetInvalid: "password_reset_invalid",
  // 413 / 415 — logo
  logoTooLarge: "logo_too_large",
  logoUnsupportedType: "logo_unsupported_type",
  // 422 — comptes
  passwordTooWeak: "password_too_weak",
  passwordTooLong: "password_too_long",
  invalidEmail: "invalid_email",
  invalidRole: "invalid_role",
  invalidCompanyName: "invalid_company_name",
  invalidAccentColor: "invalid_accent_color",
  invalidSignup: "invalid_signup",
  signupReasonRequired: "signup_reason_required",
  // 423 / 429
  accountLocked: "account_locked",
  rateLimited: "rate_limited",
  // 503
  mailUnavailable: "mail_unavailable",

  // Feuilles de temps
  notRecipient: "not_recipient",
  timesheetNotFound: "timesheet_not_found",
  timesheetLocked: "timesheet_locked",
  timesheetNotSubmitted: "timesheet_not_submitted",
  timesheetInvalidTransition: "timesheet_invalid_transition",
  timesheetInexactAmount: "timesheet_inexact_amount",
  timesheetExists: "timesheet_exists",
  timesheetEmpty: "timesheet_empty",
  timesheetFutureDay: "timesheet_future_day",
  timesheetInvalidDay: "timesheet_invalid_day",
  timesheetMonthInFuture: "timesheet_month_in_future",
  timesheetInvalidMonth: "timesheet_invalid_month",
  timesheetReasonRequired: "timesheet_reason_required",
  timesheetReasonTooLong: "timesheet_reason_too_long",
  notAConsultant: "not_a_consultant",
  notASalesRep: "not_a_sales_rep",
  alreadyAssigned: "already_assigned",
  assignmentInUse: "assignment_in_use",
  expertiseNotInProject: "expertise_not_in_project",
  notAMember: "not_a_member",

  // Paiement
  paymentMethodRequired: "payment_method_required",
  billingSuspended: "billing_suspended",
  paymentDeclined: "payment_declined",
  paymentPending: "payment_pending",
  invalidBilling: "invalid_billing",
} as const;

export type KnownProblemCode =
  (typeof PROBLEM_CODES)[keyof typeof PROBLEM_CODES];

// L'astuce `string & {}` garde l'autocomplétion des codes connus tout en
// acceptant un code inconnu du front — un serveur plus récent que lui.
export type ProblemCode = KnownProblemCode | (string & {});

/** Ce que la réponse porte en plus du document : ses en-têtes utiles. */
export type ProblemExtras = {
  /** `Retry-After`, en secondes, sur un 429 ou un 423. */
  retryAfter?: number;
};

/** Un refus métier : le serveur a répondu un document `application/problem+json`. */
export class ProblemError extends Error {
  readonly problem: Problem;
  readonly extras: ProblemExtras;

  constructor(problem: Problem, extras: ProblemExtras = {}) {
    super(problem.detail || problem.title || problem.code);
    this.name = "ProblemError";
    this.problem = problem;
    this.extras = extras;
  }

  /**
   * Délai avant nouvel essai, en secondes, quand le serveur l'a dit : en-tête
   * `Retry-After`, ou `retry_after` du corps. `undefined` sinon.
   */
  get retryAfter(): number | undefined {
    return this.extras.retryAfter ?? this.problem.retry_after ?? undefined;
  }

  /** Jeton machine stable. C'est sur lui qu'un écran branche son comportement. */
  get code(): ProblemCode {
    return this.problem.code;
  }

  /** Code HTTP, repris dans le corps par le contrat. */
  get status(): number {
    return this.problem.status;
  }

  /** Texte du refus, déjà rédigé côté serveur et affichable tel quel. */
  get detail(): string {
    return this.problem.detail;
  }

  /**
   * Identifiant de corrélation. Seul lien entre un `500` au diagnostic neutre
   * et la cause réelle journalisée : à montrer à l'utilisateur pour le support.
   */
  get requestId(): string | undefined {
    return this.problem.request_id;
  }
}

/** Le serveur n'a pas répondu, ou sa réponse était illisible. */
export class NetworkError extends Error {
  override readonly cause: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "NetworkError";
    this.cause = cause;
  }
}

/** La requête a été annulée (démontage du composant, navigation). */
export class AbortedError extends NetworkError {
  constructor(cause?: unknown) {
    super("Requête annulée.", cause);
    this.name = "AbortedError";
  }
}

export function isProblemError(e: unknown): e is ProblemError {
  return e instanceof ProblemError;
}

export function isNetworkError(e: unknown): e is NetworkError {
  return e instanceof NetworkError;
}

export function isAbortedError(e: unknown): e is AbortedError {
  return e instanceof AbortedError;
}

/**
 * Code d'un refus, ou `null` si l'erreur n'en est pas un.
 *
 * ```ts
 * if (problemCode(err) === PROBLEM_CODES.insufficientFunds) { … }
 * ```
 */
export function problemCode(e: unknown): ProblemCode | null {
  return isProblemError(e) ? e.code : null;
}

/** Extensions chiffrées d'un refus `insufficient_funds`. */
export type InsufficientFunds = {
  /** Seau visé par le refus. */
  bucket: Bucket;
  /** Montant demandé, en tokens (chaîne décimale). */
  requested: Int64;
  /** Montant réellement présent dans le seau (chaîne décimale). */
  available: Int64;
};

/** Extensions chiffrées d'un refus `envelope_overrun`. */
export type EnvelopeOverrun = {
  /** Montant demandé, en tokens (chaîne décimale). */
  requested: Int64;
  /** Reliquat d'enveloppe au moment du refus (chaîne décimale). */
  remaining: Int64;
};

/**
 * Chiffres d'un refus pour solde insuffisant, ou `null`.
 *
 * C'est ce qui permet d'écrire « il vous manque 240 tokens » au lieu de « une
 * erreur est survenue » :
 *
 * ```ts
 * const manque = insufficientFunds(err);
 * if (manque) {
 *   notify(`Il vous manque ${formatTokens(subAmounts(manque.requested, manque.available))} tokens.`);
 * }
 * ```
 */
export function insufficientFunds(e: unknown): InsufficientFunds | null {
  if (!isProblemError(e) || e.code !== PROBLEM_CODES.insufficientFunds) {
    return null;
  }
  const { bucket, requested, available } = e.problem;
  if (bucket === undefined || requested === undefined || available === undefined) {
    return null;
  }
  return { bucket, requested, available };
}

/**
 * Chiffres d'un refus pour dépassement d'enveloppe, ou `null`.
 *
 * Dépasser une enveloppe est refusé, jamais absorbé sur le disponible : ces
 * deux montants disent à l'utilisateur de combien il dépasse.
 */
export function envelopeOverrun(e: unknown): EnvelopeOverrun | null {
  if (!isProblemError(e) || e.code !== PROBLEM_CODES.envelopeOverrun) {
    return null;
  }
  const { requested, remaining } = e.problem;
  if (requested === undefined || remaining === undefined) {
    return null;
  }
  return { requested, remaining };
}

/** Champ refusé par un `invalid_field`, à rattacher au `Field` correspondant. */
export function invalidField(e: unknown): string | null {
  if (!isProblemError(e) || e.code !== PROBLEM_CODES.invalidField) {
    return null;
  }
  return e.problem.field ?? null;
}

/**
 * Texte prêt à afficher pour n'importe quelle erreur.
 *
 * Les refus métier portent déjà un `detail` rédigé côté serveur : on le reprend
 * plutôt que d'écrire un second message qui dirait la même chose moins bien.
 * Les pannes réseau, elles, n'ont rien à dire d'utile : une phrase fixe suffit.
 */
export function errorMessage(e: unknown): string {
  if (isProblemError(e)) {
    return e.detail || e.problem.title;
  }
  if (isAbortedError(e)) {
    return "Requête interrompue.";
  }
  if (isNetworkError(e)) {
    return "Le service ne répond pas. Vérifiez votre connexion, puis réessayez.";
  }
  if (e instanceof Error) {
    return e.message;
  }
  return "Une erreur inattendue est survenue.";
}

/**
 * Vrai si l'erreur vaut la peine d'être réessayée telle quelle.
 *
 * Un `409` est rejouable une fois l'état changé, mais pas « tout de suite » :
 * il n'est donc pas listé ici. Un bouton « Réessayer » ne se propose que pour
 * ce qui a une chance d'aboutir sans rien changer.
 */
export function isRetryable(e: unknown): boolean {
  if (isAbortedError(e)) {
    return false;
  }
  if (isNetworkError(e)) {
    return true;
  }
  if (isProblemError(e)) {
    return e.status >= 500 || e.status === 408;
  }
  return false;
}
